import { Video, MessageSquare, Phone } from "lucide-react";

const IST = "Asia/Kolkata";

export const LIVE_STATUSES = ["live", "waiting_for_lawyer", "waiting_for_user"];

export const STATUS_BADGE = {
  pending: ["Pending", "bg-amber-50 text-amber-700 border-amber-200"],
  confirmed: ["Confirmed", "bg-sky-50 text-sky-700 border-sky-200"],
  accepted: ["Accepted", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  live: ["Live", "bg-rose-50 text-rose-700 border-rose-200"],
  waiting_for_lawyer: ["Client waiting", "bg-rose-50 text-rose-700 border-rose-200"],
  waiting_for_user: ["Waiting for client", "bg-amber-50 text-amber-700 border-amber-200"],
  completed: ["Completed", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  cancelled: ["Cancelled", "bg-slate-100 text-slate-600 border-slate-200"],
  rejected: ["Rejected", "bg-slate-100 text-slate-600 border-slate-200"],
  no_show: ["Client no-show", "bg-rose-50 text-rose-700 border-rose-200"],
  lawyer_no_show: ["Missed by me", "bg-rose-100 text-rose-800 border-rose-200"],
  refunded: ["Refunded", "bg-slate-100 text-slate-600 border-slate-200"],
};

export const TYPE_ICON = { video: Video, audio: Phone, chat: MessageSquare };

const PALETTE = ["bg-sky-100 text-sky-800", "bg-emerald-100 text-emerald-800", "bg-rose-100 text-rose-800", "bg-amber-100 text-amber-800", "bg-violet-100 text-violet-800"];

export function initials(name) {
  return (name || "C").trim().split(/\s+/).map((x) => x[0] || "").slice(0, 2).join("").toUpperCase() || "C";
}
export function avatarTone(name) {
  let h = 0; for (const ch of name || "") h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

export const fmtAmount = (c) => `₹${Number(c.amount || 0).toLocaleString("en-IN")}`;
export const typeLabel = (t) => ({ video: "Video", audio: "Audio", chat: "Chat" }[t] || t || "—");

// Slot time in IST ("16:00" → "4:00 PM"); falls back to the UTC start / creation time.
export function fmtTime(c) {
  const iso = c.slot_start_utc || c.started_at || c.created_at;
  if (c.slot_time && !c.slot_start_utc) {
    const [h, m] = c.slot_time.split(":").map(Number);
    return new Date(Date.UTC(2000, 0, 1, h, m)).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "UTC" });
  }
  return iso ? new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: IST }) : "—";
}
export const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "numeric", minute: "2-digit", hour12: true, timeZone: IST }).replace(/ (am|pm)/, (m) => m.toUpperCase()) : "—";

const istDate = (iso) => new Date(iso).toLocaleDateString("en-CA", { timeZone: IST });  // YYYY-MM-DD

// ---- Action windows (mirror backend services/consultation_window.py; instant bookings are never gated) ----
export const EARLY_JOIN_MIN = 5;
export const NO_SHOW_AFTER_MIN = 10;
export const INSTANT_WAIT_MIN = 10;
const MIN = 60000;

/** Instant booking the lawyer hasn't started yet → { deadline, remainingMin, overdue } (mirrors lawyer_no_show.py: paid_at + 10 min). */
export function instantWait(c, now = new Date()) {
  if (c.booking_type !== "instant" || !c.paid_at || !["pending", "confirmed", "accepted"].includes(c.status)) return null;
  const deadline = new Date(c.paid_at).getTime() + INSTANT_WAIT_MIN * MIN;
  const remainingMin = Math.ceil((deadline - now.getTime()) / MIN);
  return { deadline: new Date(deadline), remainingMin: Math.max(0, remainingMin), overdue: remainingMin <= 0 };
}

/** Scheduled slot start as a Date: slot_start_utc, else slot_date + slot_time interpreted as IST. */
export function slotStart(c) {
  if (c.slot_start_utc) return new Date(c.slot_start_utc);
  if (c.slot_date && c.slot_time) return new Date(`${c.slot_date}T${c.slot_time.length === 5 ? c.slot_time : `0${c.slot_time}`}:00+05:30`);
  return null;
}
const scheduled = (c) => c.booking_type !== "instant" && slotStart(c) instanceof Date && !Number.isNaN(slotStart(c).getTime());

/** { canStart, canNoShow, canReject, opensAt } for the row's lifecycle buttons at `now`. */
export function actionWindow(c, now = new Date()) {
  if (!scheduled(c)) return { canStart: true, canNoShow: true, canReject: true, opensAt: null };
  const s = slotStart(c).getTime();
  const end = s + (Number(c.duration_minutes) || 15) * MIN;
  const t = now.getTime();
  const opensAt = s - EARLY_JOIN_MIN * MIN;
  return {
    canStart: t >= opensAt && t <= end,
    canNoShow: t >= s + NO_SHOW_AFTER_MIN * MIN,
    canReject: t < s,
    opensAt: t < opensAt ? new Date(opensAt) : null,
  };
}

/** "Starts 4:00 PM" / "Starts in 12 min" for a locked Start button. */
export function opensLabel(opensAt, now = new Date()) {
  const diff = opensAt.getTime() - now.getTime();
  if (diff < 60 * MIN) return `Starts in ${Math.max(1, Math.ceil(diff / MIN))} min`;
  const sameDay = istDate(opensAt.toISOString()) === istDate(now.toISOString());
  const time = slotTimeLabel(new Date(opensAt.getTime() + EARLY_JOIN_MIN * MIN));
  return sameDay ? `Starts ${time}` : `Starts ${opensAt.toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: IST })} · ${time}`;
}
const slotTimeLabel = (d) => d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: IST });

/** Group bookings by IST day → [{ key, title: Today|Tomorrow|weekday, date: "Thu, 01 Oct 2026", items }], soonest first. */
export function groupByDay(items) {
  const now = new Date();
  const today = istDate(now.toISOString());
  const tomorrow = istDate(new Date(now.getTime() + 86400000).toISOString());
  const map = new Map();
  for (const c of items) {
    const key = c.slot_date || istDate(c.slot_start_utc || c.created_at);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(c);
  }
  return [...map.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([key, list]) => {
    const d = new Date(`${key}T00:00:00+05:30`);
    const title = key === today ? "Today" : key === tomorrow ? "Tomorrow" : d.toLocaleDateString("en-IN", { weekday: "long", timeZone: IST });
    return { key, title, date: d.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric", timeZone: IST }),
             items: list.slice().sort((x, y) => String(x.slot_start_utc || x.slot_time || "").localeCompare(String(y.slot_start_utc || y.slot_time || ""))) };
  });
}
